import typia from 'typia'

import {
  type BoundDeviceCategoryFieldsFragment,
  type BoundDeviceCategoryType,
  type CatalogQuery,
  type ProductSetFieldsFragment,
  type UnboundDeviceCategoryType,
} from '@/graphql/creator-platform/generated'

export interface ProductSetGroup {
  readonly groupKey: string
  readonly name: string
  readonly productSets: readonly ProductSetFieldsFragment[]
  readonly thumbnailUrl: string
}

export interface ProductTitle {
  readonly attributes: readonly string[]
  readonly fullProductTitle: string
  readonly product: string
}

export interface SelectedProduct {
  readonly title: ProductTitle

  readonly productSetGroups: readonly ProductSetGroup[]

  readonly productSetGroup: ProductSetGroup

  readonly productSet: ProductSetFieldsFragment

  readonly product: ProductSetFieldsFragment['products'][number]

  readonly variant: Readonly<ProductSetFieldsFragment['products'][number]['variants'][number]>
}

export type CategoryFragment = CatalogQuery['catalog'][number]
export type BoundDeviceCategoryFragment = CategoryFragment & {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  __typename: 'BoundDeviceCategoryDto'
}
export type UnboundDeviceCategoryFragment = CategoryFragment & {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  __typename: 'UnboundDeviceCategoryDto'
}
export type BrandFragment = BoundDeviceCategoryFieldsFragment['brands'][number]
export type DeviceFragment = BoundDeviceCategoryFieldsFragment['brands'][number]['devices'][number]
/**
 * 因為 fukurou 在某些裝置沒有提供顏色資訊，例如 airpods, airtag，相對應的 peacock 也沒有提供裝置圖，只好讓他可以是 null
 */
export type DeviceColorFragment = DeviceFragment['colors'][number] | null

export interface BoundDeviceProductState {
  readonly categoryType: BoundDeviceCategoryType

  readonly brandName: BrandFragment['name']

  readonly device: DeviceFragment

  readonly color: DeviceColorFragment

  readonly selectedProduct: null | SelectedProduct
}

export type UnboundDeviceProductState =
  | AquaStandUnboundDeviceProductState
  | CommonUnboundDeviceProductState

export interface CommonUnboundDeviceProductState {
  readonly categoryType: Exclude<UnboundDeviceCategoryType, UnboundDeviceCategoryType.AquaStand>

  readonly selectedProduct: null | SelectedProduct
}

export interface AquaStandUnboundDeviceProductState {
  readonly categoryType: UnboundDeviceCategoryType.AquaStand

  readonly selectedProduct: AquaStandSelectedProduct | null
}

export interface AquaStandSelectedProduct extends SelectedProduct {
  readonly variant: SelectedProduct['variant'] & {
    aquaStandPerspective: { readonly imageUrl: string; readonly previewMaskUrl: string }
  }
}

export type ProductState = BoundDeviceProductState | UnboundDeviceProductState

export type ProductStateWithSelectedProduct = ProductState & {
  readonly selectedProduct: Exclude<ProductState['selectedProduct'], null>
}

export type CategoryType = BoundDeviceCategoryType | UnboundDeviceCategoryType

export interface UnboundDeviceProductIndicator {
  readonly categoryType: UnboundDeviceCategoryType

  readonly productHandle: string

  readonly colorHandle: string
}

export interface BoundDeviceProductIndicator extends Omit<
  UnboundDeviceProductIndicator,
  'categoryType'
> {
  readonly categoryType: BoundDeviceCategoryType

  readonly deviceHandle: string

  readonly deviceColorHandle: null | string
}

export type NewProductIndicator = BoundDeviceProductIndicator | UnboundDeviceProductIndicator

export const isBoundDeviceProductIndicator = typia.createIs<BoundDeviceProductIndicator>()
